import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SettingsUpdateRequest } from '@ignite/types';
import { apiClient, readStoredUser, userKey } from './auth';

export const settingsQueryKey = (userScope: string) => ['settings-profile', userScope] as const;

export function useSettingsData() {
  const queryClient = useQueryClient();
  const storedUser = readStoredUser();
  const userScope = storedUser?.id ?? storedUser?.email ?? 'unavailable';
  const queryKey = settingsQueryKey(userScope);
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () => apiClient.settings(),
  });
  const updateSettings = useMutation({
    mutationFn: (body: SettingsUpdateRequest) => apiClient.updateSettings(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKey, settings);
      const storedUser = parseStoredUser(localStorage.getItem(userKey));
      localStorage.setItem(
        userKey,
        JSON.stringify({
          ...storedUser,
          id: settings.user.id,
          email: settings.user.email,
          display_name: settings.user.display_name,
          avatar_url: settings.user.avatar_url,
        }),
      );
    },
  });

  return {
    settingsQuery,
    updateSettings,
  };
}

function parseStoredUser(rawUser: string | null): Record<string, unknown> {
  if (!rawUser) return {};
  try {
    const parsed = JSON.parse(rawUser) as unknown;
    return isStoredUserRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isStoredUserRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
